/**
 * Keeps the document, i18next and the language store in lockstep.
 *
 * Three things must change together when the language changes:
 * `<html lang>` (drives the Arabic font and line height in CSS), `<html dir>`
 * (drives every logical-property utility, so the whole layout mirrors), and
 * i18next's active language. Doing it in one effect means they cannot drift.
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { direction, isRtl, useLanguageStore } from '@/stores/languageStore'
import type { Language } from '@/types/api'

export interface DirectionState {
  language: Language
  dir: 'rtl' | 'ltr'
  rtl: boolean
  setLanguage: (language: Language) => void
  toggleLanguage: () => void
}

export const useDirection = (): DirectionState => {
  const language = useLanguageStore((state) => state.language)
  const setLanguage = useLanguageStore((state) => state.setLanguage)
  const toggle = useLanguageStore((state) => state.toggle)
  const { i18n } = useTranslation()

  useEffect(() => {
    const root = document.documentElement
    root.lang = language
    root.dir = direction(language)
    if (i18n.language !== language) {
      void i18n.changeLanguage(language)
    }
  }, [language, i18n])

  return {
    language,
    dir: direction(language),
    rtl: isRtl(language),
    setLanguage,
    toggleLanguage: toggle,
  }
}

/** Read the active language without subscribing to direction side effects. */
export const useLanguage = (): Language => useLanguageStore((state) => state.language)
